<!--
  Summary: Global, app-wide language switcher rendered as a teleported sliding side panel that
  can be triggered either from its own optional floating button or from a parent component.

  Responsibilities:
  - Teleports an overlay + sliding panel to <body> so it can be opened from anywhere (taskbars)
  - Lists all supportedLocales from useAppI18n and highlights the currently active one
  - Calls setLocale(locale) when a language option is clicked, then closes the panel
  - Closes the panel when clicking the overlay or the explicit close button
  - Optionally renders its own floating toggle button when the showButton prop is true
  - Exposes openPanel() via defineExpose so parent components (e.g. RoleTaskbar.vue)
    can open it programmatically without the button

  Out of Scope:
  - Persisting the selected locale across sessions (handled inside useAppI18n / UseI18n.ts)
  - Registering/configuring the vue-i18n plugin itself (done in main.ts)
  - Providing the actual translated strings (handled by the locale message files)

  Dependencies: composables/UseI18n.ts (useAppI18n), vue-i18n ($t via global injection),
  types/i18n.ts (Locale type).
-->
<template>
  <div class="language-selector">
    <button
      v-if="showButton"
      class="language-toggle"
      type="button"
      :aria-label="$t('languageSelector.open')"
      @click="openPanel"
    >
      {{ $t('languageSelector.open') }}
    </button>

    <teleport to="body">
      <div
        class="language-overlay"
        :class="{ open: isOpen }"
        @click="closePanel"
      ></div>

      <aside
        class="language-panel"
        :class="{ open: isOpen }"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('languageSelector.title')"
      >
        <header class="panel-header">
          <h2 class="panel-title">{{ $t('languageSelector.title') }}</h2>
          <button type="button" class="close-button" @click="closePanel">
            {{ $t('common.actions.close') }}
          </button>
        </header>

        <p class="current-locale">
          {{ $t('languageSelector.current') }}:
          <strong>{{ $t(`languageSelector.languages.${currentLocale}`) }}</strong>
        </p>

        <div class="language-options">
          <button
            v-for="locale in supportedLocales"
            :key="locale"
            type="button"
            class="language-option"
            :class="{ active: locale === currentLocale }"
            @click="selectLocale(locale)"
          >
            <span class="language-code">{{ locale.toUpperCase() }}</span>
            <span class="language-name">{{ $t(`languageSelector.languages.${locale}`) }}</span>
          </button>
        </div>
      </aside>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useAppI18n } from '@/composables/UseI18n'
import type { Locale } from '@/types/i18n'

defineOptions({
  name: 'LanguageSelector',
})

defineProps<{
  showButton?: boolean
}>()

const isOpen = ref(false)
const { currentLocale, supportedLocales, setLocale } = useAppI18n()

function openPanel() {
  isOpen.value = true
  const glassTaskbar = document.querySelector('.glass-taskbar')
}

function closePanel() {
  isOpen.value = false
}

function selectLocale(locale: Locale) {
  setLocale(locale)
  closePanel()
}

defineExpose({
  openPanel,
})
</script>

<style scoped>
.language-toggle {
  position: fixed;
  right: var(--spacing-6);
  bottom: var(--spacing-6);
  z-index: 1200;
  padding: var(--spacing-3) var(--spacing-5);
  border-radius: var(--radius-xl);
  background: var(--bg-surface);
  color: var(--text-primary);
  border: 1px solid var(--btn-primary-border);
  box-shadow: var(--shadow-floating);
  transition: all var(--transition-base) ease;
}

.language-toggle:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.language-overlay {
  position: fixed;
  inset: 0;
  background: var(--bg-overlay);
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--transition-base) ease;
  z-index: 1100;
}

.language-overlay.open {
  opacity: 1;
  pointer-events: auto;
}

.language-panel {
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: min(320px, 85vw);
  background: var(--bg-surface);
  backdrop-filter: blur(16px);
  box-shadow: var(--shadow-panel);
  transform: translateX(100%);
  transition: transform var(--transition-slow) ease;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  padding: var(--spacing-6);
  gap: var(--spacing-5);
}

.language-panel.open {
  transform: translateX(0);
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-4);
}

.panel-title {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.close-button {
  padding: var(--spacing-2) var(--spacing-4);
  font-size: var(--font-size-sm);
}

.current-locale {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.language-options {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
}

.language-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-3) var(--spacing-4);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-light);
  background: var(--bg-surface);
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
  transition: all var(--transition-base) ease;
}

.language-option:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
  border-color: var(--border-medium);
}

.language-option.active {
  background: var(--bg-neutral-200);
  color: var(--text-strong);
  border-color: var(--border-strong);
}

.language-code {
  font-size: var(--font-size-xs);
  letter-spacing: 0.12em;
}

.language-name {
  font-size: var(--font-size-base);
}
</style>
